// _forms/_shared/components/ConditionalModal.js
import React, { useState, useEffect } from 'react';
import './ConditionalModal.css';

const ConditionalModal = ({
  isOpen,
  onClose,
  onSave,
  fields,
  paquetes,
  fieldId,
  existingConditions = [],
}) => {
  const [selectedField, setSelectedField] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [conditions, setConditions] = useState(existingConditions);
  const [selectedPaquete, setSelectedPaquete] = useState('');
  const [conditionOperator, setConditionOperator] = useState(
    existingConditions?.operator || 'OR'
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedField('');
      setSelectedOptions([]);
      setSelectedPaquete('');
    } else {
      setConditions(existingConditions);
      setConditionOperator(existingConditions?.operator || 'OR');
    }
  }, [isOpen, existingConditions]);

  const handleFieldChange = (fId) => {
    setSelectedField(fId);
    setSelectedOptions([]);
  };

  const handleAddAddonCondition = (addonId, addonLabel) => {
    const newCondition = {
      fieldId: `addon_${addonId}`,
      targetFieldId: parseInt(fieldId),
      label: `Addon: ${addonLabel}`,
      options: ['Seleccionado'],
      type: 'addon',
      value: 'active',
    };
    setConditions((prev) => [...prev, newCondition]);
  };

  const handleOptionChange = (option) => {
    setSelectedOptions((prev) => {
      const isAlreadySelected = prev.some(
        (o) =>
          (typeof o === 'object' ? o.label : o) ===
          (typeof option === 'object' ? option.label : option)
      );
      return isAlreadySelected
        ? prev.filter(
            (o) =>
              (typeof o === 'object' ? o.label : o) !==
              (typeof option === 'object' ? option.label : option)
          )
        : [...prev, option];
    });
  };

  const handleAddCondition = () => {
    if (selectedOptions.length === 0) {
      alert('Por favor, selecciona al menos una opción.');
      return;
    }
  
    // 🟢 FIX: Verificar si es addon ANTES del find
    const isAddon = typeof selectedField === 'string' && selectedField.startsWith('addon_');
  
    // 🟢 FIX: Solo buscar el campo si NO es addon
    const field = isAddon
      ? { label: `Addon: ${selectedField.replace('addon_', '')}` }
      : fields.find((f) => f.id === parseInt(selectedField));
  
    if (!field) {
      alert('Campo no válido');
      return;
    }
  
    // 🟢 FIX: Normalizar options a strings al guardar
    // Los datos NUEVOS serán consistentes (strings)
    // Los datos VIEJOS (objetos) siguen funcionando porque el evaluador los normaliza
    const normalizedOptions = selectedOptions.map((opt) =>
      typeof opt === 'object' && opt !== null ? opt.label : String(opt)
    );
  
    const newCondition = {
      fieldId: isAddon ? selectedField : parseInt(selectedField),
      targetFieldId: parseInt(fieldId),
      label: field.label,
      options: normalizedOptions, // ✅ Ahora siempre son strings
      type: isAddon ? 'addon' : 'field',
      value: normalizedOptions[0], // ✅ También string
    };
  
    setConditions((prev) => [...prev, newCondition]);
    setSelectedField('');
    setSelectedOptions([]);
  };

  const handleAddPaqueteCondition = () => {
    if (!selectedPaquete) {
      alert('Por favor, selecciona un paquete.');
      return;
    }
    const newCondition = {
      fieldId: 'packages',
      targetFieldId: parseInt(fieldId),
      label: 'Paquete',
      options: [selectedPaquete],
      type: 'package',
      value: selectedPaquete,
    };
    setConditions((prev) => [...prev, newCondition]);
    setSelectedPaquete('');
  };

  const handleRemoveCondition = (index) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave({ conditions, operator: conditionOperator });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Configurar Condiciones</h2>
        <button className="close-button" onClick={onClose}>
          Cerrar
        </button>

        <div className="modal-section">
          <h3>Lógica entre condiciones</h3>
          <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
            <strong>O (OR):</strong> mostrar si se cumple <em>cualquiera</em> de las condiciones.
            <br />
            <strong>Y (AND):</strong> mostrar solo si se cumplen <em>todas</em> las condiciones.
          </p>
          <div className="operator-toggle">
            <button
              className={`operator-btn ${conditionOperator === 'OR' ? 'active' : ''}`}
              onClick={() => setConditionOperator('OR')}
              type="button"
            >
              O (OR) — cualquiera
            </button>
            <button
              className={`operator-btn ${conditionOperator === 'AND' ? 'active' : ''}`}
              onClick={() => setConditionOperator('AND')}
              type="button"
            >
              Y (AND) — todas
            </button>
          </div>
        </div>

        <div className="modal-section">
          <h3>Selecciona un campo y las opciones</h3>
          <select onChange={(e) => handleFieldChange(e.target.value)} value={selectedField}>
            <option value="">Seleccionar Campo</option>
            {fields.map((field) => (
              <option key={field.id} value={field.id}>
                {field.label}
              </option>
            ))}
          </select>
          {selectedField && (
            <div className="options-list">
              <h4>
                Opciones para{' '}
                {fields.find((f) => f.id === parseInt(selectedField))?.label}
              </h4>
              {fields
                .find((f) => f.id === parseInt(selectedField))
                ?.options.map((option, idx) => (
                  <div key={idx} className="option-item">
                    <input
                      type="checkbox"
                      checked={selectedOptions.some(
                        (o) =>
                          (typeof o === 'object' ? o.label : o) ===
                          (typeof option === 'object' ? option.label : option)
                      )}
                      onChange={() => handleOptionChange(option)}
                    />
                    <label>{typeof option === 'object' ? option.label : option}</label>
                  </div>
                ))}
              <button className="add-button" onClick={handleAddCondition}>
                Agregar Condición
              </button>
            </div>
          )}
        </div>

        <div className="modal-section">
          <h4>Seleccionar Paquete como condición</h4>
          <select onChange={(e) => setSelectedPaquete(e.target.value)} value={selectedPaquete}>
            <option value="">Seleccionar Paquete</option>
            {paquetes.map((paquete) => (
              <option key={paquete.id} value={paquete.id}>
                {paquete.title || paquete.name}
              </option>
            ))}
          </select>
          <button className="add-button" onClick={handleAddPaqueteCondition}>
            Agregar Condición de Paquete
          </button>
        </div>

        <div className="modal-section">
          <h4>Seleccionar Addon como condición</h4>
          <p style={{ fontSize: '0.75rem', color: '#666' }}>
            El campo se mostrará si el addon tiene una cantidad mayor a 0.
          </p>
          <select
            onChange={(e) => {
              const selected = e.target.value;
              if (selected) {
                const addonLabel = paquetes
                  .flatMap((p) => p.addons || [])
                  .find((a) => a.id.toString() === selected)?.label;
                handleAddAddonCondition(selected, addonLabel);
                e.target.value = '';
              }
            }}
          >
            <option value="">Seleccionar Addon</option>
            {Array.from(
              new Set(paquetes.flatMap((p) => p.addons || []).map((a) => a.id))
            ).map((id) => {
              const addon = paquetes
                .flatMap((p) => p.addons || [])
                .find((a) => a.id === id);
              return (
                <option key={id} value={id}>
                  {addon.label}
                </option>
              );
            })}
          </select>
        </div>

        <div className="modal-section">
          <h3>Condiciones Guardadas:</h3>
          {conditions.length > 0 ? (
            <ul className="conditions-list">
              {conditions.map((condition, index) => (
                <li key={index} className="condition-item">
                  {condition.label}:{' '}
                  {condition.options
                    ? condition.options
                        .map((opt) => (typeof opt === 'object' ? opt.label : opt))
                        .join(', ')
                    : typeof condition.value === 'object'
                    ? condition.value.label
                    : condition.value}
                  <button
                    className="remove-button"
                    onClick={() => handleRemoveCondition(index)}
                  >
                    Eliminar
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No hay condiciones guardadas.</p>
          )}
        </div>

        <button className="save-button" onClick={handleSave}>
          Guardar Condiciones
        </button>
      </div>
    </div>
  );
};

export default ConditionalModal;